// src/oura/components/ScanVersions.tsx
//
// Which scan you are looking at (#3933).
//
// Every rescan has always been kept — `POST /businesses/{id}/rescan` inserts a
// row and nothing deletes the old ones — but the workspace only ever read
// `latest_assessment`, so from the outside a rescan looked like it had
// overwritten the last one. The only trace of the others was the history chart.
//
// A PAST SCAN MUST NEVER BE MISTAKEN FOR THE CURRENT ONE. That is the whole
// risk this control introduces: every figure on the screen is suddenly about a
// different day, and nothing else on the page says so. Hence a banner rather
// than a quiet dropdown state, and hence the banner naming the date rather than
// saying "historical".

import { ArrowDownIcon, ArrowRightIcon, ArrowUpIcon, CheckIcon, ChevronDownIcon, ClockIcon, HistoryIcon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import type { AeoScoreHistoryEntry } from "@service/aeo";
import { shortDate } from "../lib/oura";
import { cn } from "../lib/cn";
import { Button } from "../primitives/Button";
import { PillButton, Tag } from "@oura/ui";
import { MERCHANT_V2 } from "../merchantV2";
import { clientScans, versionNumbers } from "../visibility/questionSets";
import { useOuraRole } from "../role";

/** A run that produced no report. It is still listed — a scan that failed is a
 *  fact about the business's history — but it is labelled and never scored. */
function unscored(entry: AeoScoreHistoryEntry): string | null {
    if (entry.status === "cancelled") return "cancelled";
    if (entry.status === "failed") return "failed";
    if (entry.gated) return "gated";
    return null;
}

/**
 * Each scan's V number, from the backend (ourteam-backend#5900): the same
 * numbers its "AI Visibility Report V1, V2" titles carry. A run with no
 * report (failed, cancelled, partial, gated) has none, and a backend that
 * sends no numbers gets none either: absent is unknown, so no number is
 * invented here that a report title could contradict.
 */
export function scanNumbers(history: AeoScoreHistoryEntry[]): Map<string, number> {
    return new Map(history.flatMap((h) => (typeof h.scan_number === "number" ? [[h.assessment_id, h.scan_number] as const] : [])));
}

/** The scored scan just before this one, to measure the change against. */
function previousScored(history: AeoScoreHistoryEntry[], entry: AeoScoreHistoryEntry): AeoScoreHistoryEntry | null {
    const at = new Date(entry.captured_at).getTime();
    return (
        history
            .filter((h) => !unscored(h) && new Date(h.captured_at).getTime() < at)
            .sort((a, b) => new Date(b.captured_at).getTime() - new Date(a.captured_at).getTime())[0] ?? null
    );
}

/** One score, and its move since the scan before: green up, red down, nothing when level or first. */
function Score({ label, value, before }: { label: string; value: number | null | undefined; before: number | null | undefined }) {
    if (typeof value !== "number") return null;
    const change = typeof before === "number" ? value - before : 0;
    return (
        <span className="inline-flex items-center gap-1 whitespace-nowrap tabular-nums">
            <span className="text-gray-500">{label}</span>
            <span className="font-medium text-gray-900">{value}</span>
            {change !== 0 && (
                <span className={cn("inline-flex items-center", change > 0 ? "text-emerald-600" : "text-rose-600")}>
                    {change > 0 ? <ArrowUpIcon aria-hidden className="h-3 w-3" /> : <ArrowDownIcon aria-hidden className="h-3 w-3" />}
                    {Math.abs(change)}
                    <span className="sr-only">{change > 0 ? " up" : " down"} since the scan before</span>
                </span>
            )}
        </span>
    );
}

/**
 * The picker. Renders nothing when there is only one scan: a selector that
 * cannot select is a control that teaches a reader there is something to
 * choose when there is not.
 */
function MerchantV2ScanVersionPicker({
    history,
    selectedId,
    latestId,
    onOpen,
}: {
    history: AeoScoreHistoryEntry[];
    /** null = the latest, which is what no selection means. */
    selectedId: string | null;
    latestId: string | null;
    onOpen: (id: string) => void;
}) {
    const [open, setOpen] = useState(false);
    // The version of each scan by its question set, as the chart and the staff page number it (#4657).
    const numbers = useMemo(() => versionNumbers(clientScans(history)), [history]);
    if (history.length < 2) return null;
    const current = history.find((h) => h.assessment_id === (selectedId ?? latestId)) ?? history[0];
    const currentN = numbers.get(current.assessment_id);

    return (
        <div className="relative">
            {/* A pill, like the header actions it sits beside. */}
            <PillButton icon={HistoryIcon} onClick={() => setOpen((o) => !o)} aria-expanded={open}>
                {currentN ? `V${currentN} · ` : ""}Scan of {shortDate(current.captured_at)}
                <ChevronDownIcon aria-hidden className="h-3.5 w-3.5" />
            </PillButton>
            {open && (
                <>
                    <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
                    <div className="absolute right-0 z-40 mt-1 max-h-80 w-[22rem] max-w-[calc(100vw-2rem)] overflow-y-auto rounded-lg border border-gray-200 bg-white py-1 shadow-lg">
                        {history.map((h) => {
                            const isCurrent = h.assessment_id === (selectedId ?? latestId);
                            const reason = unscored(h);
                            const n = numbers.get(h.assessment_id);
                            const before = reason ? null : previousScored(history, h);
                            return (
                                <button
                                    key={h.assessment_id}
                                    onClick={() => { onOpen(h.assessment_id); setOpen(false); }}
                                    aria-current={isCurrent ? "true" : undefined}
                                    // The selected row is a state, not a target: it keeps its
                                    // blue on hover, and a tick says it is the one on screen.
                                    className={cn(
                                        "flex w-full items-center justify-between gap-3 px-3 py-2.5 text-left transition-colors",
                                        isCurrent ? "cursor-default bg-blue-50" : "hover:bg-gray-50",
                                    )}
                                >
                                    <span className="flex min-w-0 items-center gap-2">
                                        <CheckIcon aria-hidden className={cn("h-4 w-4 shrink-0", isCurrent ? "text-blue-600" : "invisible")} />
                                        <span className={cn("truncate text-sm", isCurrent ? "font-semibold text-blue-700" : "text-gray-900")}>
                                            {n ? `V${n} · ` : ""}Scan of {shortDate(h.captured_at)}
                                        </span>
                                        {h.assessment_id === latestId && <Tag tone="blue">Latest</Tag>}
                                    </span>
                                    {/* The scores on the right, with their move since the scan before. */}
                                    {reason ? (
                                        <span className="shrink-0 text-xs text-amber-700">{reason}</span>
                                    ) : (
                                        <span className="flex shrink-0 flex-col items-end gap-0.5 text-xs">
                                            <Score label="AEO" value={h.aeo_score} before={before?.aeo_score} />
                                            <Score label="SEO" value={h.seo_score} before={before?.seo_score} />
                                        </span>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                </>
            )}
        </div>
    );
}

function scoreLabel(entry: AeoScoreHistoryEntry): string {
    const reason = unscored(entry);
    if (reason) return reason;
    const parts = [
        typeof entry.aeo_score === "number" ? `AEO ${entry.aeo_score}` : null,
        typeof entry.seo_score === "number" ? `SEO ${entry.seo_score}` : null,
    ].filter(Boolean);
    return parts.length ? parts.join(" · ") : "no score";
}

/** The picker before the redesign, unchanged: what every screen shows with MERCHANT_V2 off. */
function LegacyScanVersionPicker({
    history,
    selectedId,
    latestId,
    onOpen,
}: {
    history: AeoScoreHistoryEntry[];
    /** null = the latest, which is what no selection means. */
    selectedId: string | null;
    latestId: string | null;
    onOpen: (id: string) => void;
}) {
    const [open, setOpen] = useState(false);
    if (history.length < 2) return null;
    const current = history.find((h) => h.assessment_id === (selectedId ?? latestId)) ?? history[0];

    return (
        <div className="relative">
            {/* A pill, like the header actions it sits beside. */}
            <PillButton icon={HistoryIcon} onClick={() => setOpen((o) => !o)} aria-expanded={open}>
                Scan of {shortDate(current.captured_at)}
                <ChevronDownIcon aria-hidden className="h-3.5 w-3.5" />
            </PillButton>
            {open && (
                <>
                    <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
                    <div className="absolute right-0 z-40 mt-1 max-h-80 w-72 overflow-y-auto rounded-lg border border-gray-200 bg-white py-1 shadow-lg">
                        {history.map((h) => {
                            const isCurrent = h.assessment_id === (selectedId ?? latestId);
                            const reason = unscored(h);
                            return (
                                <button
                                    key={h.assessment_id}
                                    onClick={() => { onOpen(h.assessment_id); setOpen(false); }}
                                    className={cn(
                                        "flex w-full items-baseline justify-between gap-3 px-3 py-2 text-left transition-colors hover:bg-gray-50",
                                        isCurrent && "bg-blue-50",
                                    )}
                                >
                                    <span className="min-w-0">
                                        <span className={cn("block truncate text-sm", isCurrent ? "font-semibold text-blue-700" : "text-gray-900")}>
                                            {shortDate(h.captured_at)}
                                            {h.assessment_id === latestId && (
                                                <span className="ml-1.5 text-xs font-normal text-gray-500">latest</span>
                                            )}
                                        </span>
                                        <span className={cn("block text-xs", reason ? "text-amber-700" : "text-gray-500")}>
                                            {scoreLabel(h)}
                                        </span>
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                </>
            )}
        </div>
    );
}

type ScanVersionPickerProps = Parameters<typeof MerchantV2ScanVersionPicker>[0];

/**
 * The picker. Renders nothing when there is only one scan: a selector that
 * cannot select is a control that teaches a reader there is something to
 * choose when there is not. The redesign's picker (V numbers, score moves)
 * only for a merchant with MERCHANT_V2 on, the same test PastScanBar is
 * shown on. Staff always keep the picker as it was.
 */
export function ScanVersionPicker(props: ScanVersionPickerProps) {
    const role = useOuraRole();
    const merchant = MERCHANT_V2 && role !== "oura-staff";
    return merchant ? <MerchantV2ScanVersionPicker {...props} /> : <LegacyScanVersionPicker {...props} />;
}

/**
 * The banner. Not dismissible, and it names the date.
 *
 * Every number under it belongs to a different day, and a reader who arrived
 * from a shared link has no other way to know that. A run that ended without
 * a report (failed, cancelled, gated) has no figures to date, so it says that
 * instead, and the way back is the same button.
 */
export function PastScanBanner({
    capturedAt,
    unscored,
    onBackToLatest,
}: {
    capturedAt?: string | null;
    /** The scan on screen produced no score. */
    unscored?: boolean;
    onBackToLatest: () => void;
}) {
    return (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5">
            <p className="my-0 flex items-center gap-2 text-sm text-amber-900">
                <ClockIcon className="h-4 w-4 flex-shrink-0" />
                <span>
                    Viewing a past scan{capturedAt ? ` of ${shortDate(capturedAt)}` : ""}.{" "}
                    {unscored
                        ? "This scan produced no score."
                        : "Nothing here can be changed, and the figures are what this business looked like then."}
                </span>
            </p>
            <Button variant="outline" size="sm" onClick={onBackToLatest}>Back to latest</Button>
        </div>
    );
}

/** The merchant shell's slot across the top of the content column (oura-auth.tsx). */
export const MERCHANT_TOPBAR_ID = "oura-merchant-topbar";

/**
 * The merchant's past-scan notice (redesign): a thin bar across the top of
 * the content, centred, pinned while the page scrolls, like the Trainer's
 * status bars. One sentence and the way back. It portals into the shell's
 * slot, and renders in place where there is none.
 */
export function PastScanBar({ capturedAt, onBackToLatest }: { capturedAt?: string | null; onBackToLatest: () => void }) {
    const [slot, setSlot] = useState<HTMLElement | null>(null);
    useEffect(() => setSlot(document.getElementById(MERCHANT_TOPBAR_ID)), []);
    const bar = (
        <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 border-b border-amber-200 bg-amber-50 px-4 py-2 text-center text-xs font-medium text-amber-900">
            <span>Currently previewing past scan{capturedAt ? ` from ${shortDate(capturedAt)}` : ""}.</span>
            <button type="button" onClick={onBackToLatest} className="inline-flex items-center gap-1 rounded text-blue-600 hover:text-blue-800 hover:underline">
                View latest scan
                <ArrowRightIcon aria-hidden className="h-3.5 w-3.5" />
            </button>
        </div>
    );
    return slot ? createPortal(bar, slot) : <div className="mb-4">{bar}</div>;
}

/** The id could not be opened: another business's scan, or one that is gone. */
export function ScanNotFoundBanner({ onBackToLatest }: { onBackToLatest: () => void }) {
    return (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-gray-200 bg-gray-50 px-4 py-2.5">
            <p className="my-0 text-sm text-gray-700">
                That scan could not be opened: it may belong to another business, or no longer exist.
                Showing the latest instead.
            </p>
            <Button variant="outline" size="sm" onClick={onBackToLatest}>Dismiss</Button>
        </div>
    );
}
